// Commons imports.
import { SERVLET_COMMAND, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { SERVLET_ACTION, ServletActions } from '../../NonReact/constants/React/ServletActions/ServletActions'
import { getBaseServletPath } from '../../NonReact/constants/ApiConstants'
import { requestGetJSON } from '../../NonReact/common/API/REST/RESTAPIClient'
import { mapRequestResults } from '../../NonReact/common/API/REST/RESTClient'
import { requestMultiPart } from '../Common/API/MultiPartClient'
import { RESTAPIError } from '../../NonReact/common/API/REST/RESTAPIError'
import { JSONResponse } from '../../NonReact/common/Types/Types'
import { RequestResults } from '../../NonReact/common/Types/MainMenu/ReportListTypes'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../NonReact/utils/URLBuilder'
// Sub imports.
import { CSVResultData } from './Types'

const RES_TYPE_PARAM: string = 'ResType'
const RES_TYPE_JSON: string = 'JSON'

/**
 * Builds the legacy servlet JSON URL for bulk-add-users operations.
 *
 * @param servletMapping Branding servlet segment (for example {@code Akixi2019}).
 * @param action Servlet {@code Action} query value.
 * @returns URL constructor including {@code ServletCmd}, {@code Action}, and {@code ResType=JSON}.
 */
const bulkAddUsersServletURL = (servletMapping: string, action: ServletActions): URLConstructor =>
    getBaseServletPath(servletMapping).appendQueryParams({
        [SERVLET_COMMAND]: ServletCmds.BULK_ADD_USERS,
        [SERVLET_ACTION]: action,
        [RES_TYPE_PARAM]: RES_TYPE_JSON
    })

/**
 * Uploads file in to temperary file location.
 *
 * @param servletPath Branding servlet segment passed from the host page (same as legacy {@code sSERVLETPATH} basename).
 * @param file The CSV file to upload.
 */
export const uploadCSVFile = async (servletPath: string, file: File): Promise<CSVResultData> => {
    const json: Nullable<JSONResponse<RequestResults<CSVResultData>>> = await requestMultiPart<
        JSONResponse<RequestResults<CSVResultData>>
    >(bulkAddUsersServletURL(servletPath, ServletActions.CSV_FILE_UPLOAD), { 'files[]': file })

    const result: Definable<CSVResultData> = mapRequestResults<CSVResultData>(json ?? null, true)

    if (IsVarEmpty(result)) throw new RESTAPIError(0, null, 'No result data', null)

    return result
}

/**
 * Confirms the file uploaded.
 *
 * @param servletPath Branding servlet segment passed from the host page.
 * @param isErrorIgnored Flag defining if the errors should be ignored and still be passed and dealt with.
 */
export const confirmCSVFile = (servletPath: string, isErrorIgnored: boolean): Promise<Definable<CSVResultData>> =>
    requestGetJSON<RequestResults<CSVResultData>>(
        bulkAddUsersServletURL(servletPath, ServletActions.CONFIRM_FILE)
    ).then(
        (json: JSONResponse<RequestResults<CSVResultData>>): Definable<CSVResultData> =>
            mapRequestResults<CSVResultData>(json, isErrorIgnored)
    )

/**
 * Cancel file upload.
 *
 * @param servletPath Branding servlet segment passed from the host page.
 */
export const cancelCSVFile = (servletPath: string): Promise<Definable<CSVResultData>> =>
    requestGetJSON<RequestResults<CSVResultData>>(bulkAddUsersServletURL(servletPath, ServletActions.CANCEL_FILE)).then(
        (json: JSONResponse<RequestResults<CSVResultData>>): Definable<CSVResultData> =>
            mapRequestResults<CSVResultData>(json, false)
    )
